import { useEffect, useState } from "react";
import { icons } from "utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  getProjects,
  getQueryLogs,
  getSubscription,
  getDeletedProject,
} from "store/globalSlice";
import { getDataFromLocalStorage, titleCaseString } from "utils/helpers";
import { ceil, orderBy } from "lodash";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import "./Usage.scss";

ChartJS.register(ArcElement, Tooltip, Legend);

const Usage = () => {
  const dispatch = useDispatch();
  const reduxData = useSelector((state) => state.global);
  const { isSubscription, isDarkMode } = reduxData || {};
  const latestAccount = getDataFromLocalStorage("address");
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    const projectRes = await dispatch(getProjects());
    const queryRes = await dispatch(getQueryLogs());
    const subscriptionRes = await dispatch(getSubscription());
    const deletedProject = await dispatch(getDeletedProject());
    const selectedPlan = subscriptionRes?.data?.find(
      (o) =>
        o.sub_tier_id === getDataFromLocalStorage("subscription")?.sub_tier_id
    );
    const { used, to_vest, available } = queryRes?.data || {};
    const totalQuery = used + available + to_vest;
    const usedQuery = ceil((used / selectedPlan?.max_queries_per_day) * 100);
    setPageData({
      planName: titleCaseString(selectedPlan?.name),
      totalQuery: totalQuery || 0,
      totalQueryLimit: selectedPlan?.max_queries_per_day || 0,
      totalProjectLimit:
        selectedPlan?.project_limit < 10
          ? `0${selectedPlan?.project_limit}`
          : selectedPlan?.project_limit,
      used: usedQuery,
      available: 100 - usedQuery,
      usedCount: used,
      availableCount: selectedPlan?.monthly_query_limit - used,
      cost: `${selectedPlan?.min_fund_per_month} FUND/month`,
      deleteProject: deletedProject?.data || [],
      projects: orderBy(
        projectRes?.data || [],
        ["Project.query_count", "Project.name"],
        ["desc", "asc"]
      ),
    });
    setLoading(false);
  };

  useEffect(() => {
    if (isSubscription) {
      setLoading(true);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestAccount, isSubscription]);

  const {
    totalQuery,
    totalQueryLimit,
    used,
    available,
    planName,
    cost,
    totalProjectLimit,
    projects,
    deleteProject,
  } = pageData;

  return (
    <div
      id="usage-container"
      className={`cp-38 ${isDarkMode ? "custom-scroll-dark" : "custom-scroll"}`}
    >
      {loading ? (
        <div className="row">
          <div className="mb-3">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-35 br-4`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-200 br-20`}
            />
          </div>
          <div className="col-md-8 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-200 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
          <div className="col-md-4 cmb-30">
            <div
              className={`${
                isDarkMode ? "skeleton-dark" : "skeleton"
              } h-130 br-20`}
            />
          </div>
        </div>
      ) : (
        <>
          <div
            className={`text-20-700 ${
              isDarkMode ? "color-ffff" : "color-2749"
            } mb-3`}
          >
            My Plan
          </div>
          <div className="row un">
            <div className="col-md-4 col-12">
              <div
                className={`${isDarkMode ? "" : "b-dee3"} br-20 ${
                  isDarkMode ? "" : "cps-20 cpe-20 cpb-20 cpt-30"
                } ${isDarkMode ? "" : "fb-center h-100"} ${
                  isDarkMode ? "block1" : ""
                }`}
              >
                <div
                  className={`${
                    isDarkMode ? "dark-block1 fb-center h-100" : ""
                  }`}
                >
                  <div
                    className={`text-22-400 ${
                      isDarkMode ? "color-c9c9" : "color-7e91"
                    }`}
                  >
                    Total Queries
                  </div>
                  <div
                    className={`text-26-500 ${
                      isDarkMode ? "color-ffff" : "color-2749"
                    } cmb-50`}
                  >
                    {totalQuery?.toLocaleString()}
                  </div>

                  <div className="fa-center gap-2 mb-2">
                    <span className="bg-56ff h-15 br-8 w-26 d-flex" />
                    <span
                      className={`text-18-600 ${
                        isDarkMode ? "color-ffff" : "color-7e91"
                      }`}
                    >
                      {used}%
                    </span>
                    <span className="text-15-500 color-b5c0">Used</span>
                  </div>
                  <div className="fa-center gap-2">
                    <span
                      className="h-15 br-8 w-26 d-flex"
                      style={{ background: "#6c56ff66" }}
                    />
                    <span
                      className={`text-18-600 ${
                        isDarkMode ? "color-ffff" : "color-7e91"
                      }`}
                    >
                      {available}%
                    </span>
                    <span className="text-15-500 color-b5c0">Available</span>
                  </div>
                </div>
                <div className="h-150">
                  <Doughnut
                    data={{
                      labels: ["Used", "Available"],
                      datasets: [
                        {
                          label: "Query",
                          data: [used, available],
                          backgroundColor: ["#6c56ff", "#6c56ff66"],
                          borderWidth: 1,
                          cutout: "60%",
                          borderRadius: 8,
                          offset: 20,
                        },
                      ],
                    }}
                    options={{
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-8 col-12">
              <div
                className={`${isDarkMode ? "" : "b-dee3"} br-20 ${
                  isDarkMode ? "" : "cps-20 cpe-20 cpb-20 cpt-30"
                } ${isDarkMode ? "" : "h-100"} ${isDarkMode ? "block2" : ""}`}
              >
                <div className={`${isDarkMode ? "dark-block2 h-100" : ""}`}>
                  <div
                    className={`text-22-400 ${
                      isDarkMode ? "color-c9c9" : "color-7e91"
                    }`}
                  >
                    {planName}
                  </div>
                  <div
                    className={`text-26-500 ${
                      isDarkMode ? "color-ffff" : "color-2749"
                    } mb-3`}
                  >
                    {cost}
                  </div>
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-6">
                        <div
                          style={{
                            background: isDarkMode
                              ? "rgb(0 0 0 / 32%)"
                              : "#6c56ff1a",
                          }}
                          className="d-flex cp-20 br-8 h-100"
                        >
                          <div className="h-55 cme-20">
                            <img
                              src={icons.activeMessage}
                              alt="activeMessage"
                              className="fit-image"
                            />
                          </div>
                          <div>
                            <div
                              className={`text-20-600 ${
                                isDarkMode ? "color-ffff" : "color-2749"
                              }`}
                            >
                              {totalQueryLimit?.toLocaleString()}
                            </div>
                            <div
                              className={`text-18-400 ${
                                isDarkMode ? "color-c9c9" : "color-7e91"
                              }`}
                            >
                              Query limit
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div
                          style={{
                            background: isDarkMode
                              ? "rgb(0 0 0 / 32%)"
                              : "#6c56ff1a",
                          }}
                          className="d-flex cp-20 br-8 h-100"
                        >
                          <div className="h-55 cme-20">
                            <img
                              src={icons.activeFolder}
                              alt="activeMessage"
                              className="fit-image"
                            />
                          </div>
                          <div>
                            <div
                              className={`text-20-600 ${
                                isDarkMode ? "color-ffff" : "color-2749"
                              }`}
                            >
                              {totalProjectLimit}
                            </div>
                            <div
                              className={`text-18-400 ${
                                isDarkMode ? "color-c9c9" : "color-7e91"
                              }`}
                            >
                              Projects limit
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row cmt-30 un">
            {projects?.map((elem, index) => {
              const { name, query_count } = elem?.Project;
              return (
                <div className="col-md-4 cmb-30" key={index}>
                  <div
                    className={`${isDarkMode ? "" : "b-dee3"} ${
                      isDarkMode ? "" : "cp-30"
                    } br-20 ${isDarkMode ? "block3" : ""}`}
                  >
                    <div className={`${isDarkMode ? "dark-block3" : ""}`}>
                      <div
                        className={`text-22-600 ${
                          isDarkMode ? "color-ffff" : "color-2749"
                        } mb-2 overflow-hidden`}
                      >
                        {name?.length > 30
                          ? `${name.substring(0, 30)}...`
                          : name}
                      </div>
                      <div className="fa-center gap-2">
                        <span
                          className={`text-26-400 ${
                            isDarkMode ? "color-ffff" : "color-2749"
                          }`}
                        >
                          {query_count < 10 ? `0${query_count}` : query_count}
                        </span>
                        <span
                          className={`text-16-400 ${
                            isDarkMode ? "color-c9c9" : "color-7e91"
                          }`}
                        >
                          Queries
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {deleteProject?.length > 0 && (
            <>
              <div
                className={`text-20-700 ${
                  isDarkMode ? "color-ffff" : "color-2749"
                } mb-3`}
              >
                Deleted Projects
              </div>
              <div className="row un">
                {deleteProject?.map((elem, index) => {
                  const { name, query_count } = elem || {};
                  return (
                    <div className="col-md-4 cmb-30" key={index}>
                      <div
                        className={`b-dee3 cp-30 br-20  ${
                          isDarkMode ? "delete-block-dark" : "delete-block"
                        }`}
                      >
                        <div
                          className={`text-22-600 ${
                            isDarkMode ? "color-ffff" : "color-2749"
                          } mb-2 overflow-hidden`}
                        >
                          {name?.length > 30
                            ? `${name.substring(0, 30)}...`
                            : name}
                        </div>
                        <div className="fa-center gap-2">
                          <span
                            className={`text-26-400 ${
                              isDarkMode ? "color-ffff" : "color-2749"
                            }`}
                          >
                            {query_count < 10 ? `0${query_count}` : query_count}
                          </span>
                          <span
                            className={`text-16-400 ${
                              isDarkMode ? "color-c9c9" : "color-7e91"
                            }`}
                          >
                            Queries
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Usage;
